import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { fetchCsrfToken, clearCsrfToken, hasCsrfToken } from "../services/csrf";

interface CsrfContextType {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  refreshToken: () => Promise<void>;
}

const CsrfContext = createContext<CsrfContextType | undefined>(undefined);

export const CsrfProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initializeCsrf = useCallback(async () => {
    // Skip if already initialized
    if (hasCsrfToken()) {
      setIsInitialized(true);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await fetchCsrfToken();

      setIsInitialized(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to initialize CSRF token";
      setError(errorMessage);
      console.error("CSRF initialization error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      setError(null);
      await fetchCsrfToken();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to refresh CSRF token";
      setError(errorMessage);
      throw err;
    }
  }, []);

  useEffect(() => {
    initializeCsrf();

    return () => {
      clearCsrfToken();
    };
  }, [initializeCsrf]);

  const contextValue = useMemo(
    () => ({
      isInitialized,
      isLoading,
      error,
      refreshToken,
    }),
    [isInitialized, isLoading, error, refreshToken],
  );

  // Render children even if CSRF fails — the interceptor handles CSRF errors gracefully.
  return (
    <CsrfContext.Provider value={contextValue}>{children}</CsrfContext.Provider>
  );
};

export const useCsrf = (): CsrfContextType => {
  const context = useContext(CsrfContext);

  if (!context) {
    throw new Error("useCsrf must be used within a CsrfProvider");
  }

  return context;
};
